import React, { useEffect, useState } from "react";
import { Download, Loader2, X } from "lucide-react";

export default function ImageModal({ imageUrl, wordMark, onClose }) {
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    function onKeyDown(event) {
      if (event.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  if (!imageUrl) return null;

  const filename = `${(wordMark || "trademark-logo").replace(/[^a-z0-9-]+/gi, "-")}-logo`;

  /**
   * A plain `<a download>` just navigates the browser to imageUrl and saves
   * whatever comes back. If that URL is behind auth, CORS, or hotlink
   * protection on the source register, what comes back is an HTML error
   * page — saved as "...-logo.htm" ("File wasn't available on site").
   * Fetching the bytes ourselves gets the real image; if the fetch itself
   * is blocked (no CORS on the source), we fall back to opening it in a
   * new tab instead of producing another broken file.
   */
  async function handleDownload() {
    setDownloading(true);
    try {
      const response = await fetch(imageUrl);
      if (!response.ok) throw new Error("Image request failed");
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(blobUrl);
    } catch {
      window.open(imageUrl, "_blank", "noopener,noreferrer");
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={wordMark ? `Logo for ${wordMark}` : "Trade mark logo"}
      onClick={onClose}
      className="fixed inset-0 z-[60] flex items-center justify-center bg-ink-950/75 p-6 backdrop-blur-sm"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-lg rounded-soft bg-white p-6 shadow-float"
      >
        <div className="absolute right-3 top-3 flex items-center gap-1">
          <button
            type="button"
            onClick={handleDownload}
            disabled={downloading}
            aria-label="Download logo"
            title="Download"
            className="rounded p-1.5 text-ink-400 hover:bg-ink-100 hover:text-ink-900 disabled:cursor-wait disabled:opacity-60"
          >
            {downloading ? (
              <Loader2
                className="h-4.5 w-4.5 animate-spin"
                aria-hidden="true"
              />
            ) : (
              <Download className="h-4.5 w-4.5" aria-hidden="true" />
            )}
          </button>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded p-1.5 text-ink-400 hover:bg-ink-100 hover:text-ink-900"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <div className="flex min-h-[260px] items-center justify-center rounded border border-ink-200 bg-paper p-6">
          <img
            src={imageUrl}
            alt=""
            className="max-h-[50vh] max-w-full object-contain"
          />
        </div>

        {wordMark && (
          <p className="mt-4 text-center font-display text-lg text-ink-900">
            {wordMark}
          </p>
        )}
      </div>
    </div>
  );
}
